import { Directive, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { LocalNewsModel } from '../typings';
import { ActivityLoggerService } from '../services/activityLogger.service';
import { VideoStallHandlerService } from '../services/video-stall-handler.service';
import { SharedService } from '../services/shared.service';
import { HomeService } from '../services/home.service';

const ANIMATION_VIDEO_DELAY = 1000;

@Directive()
export abstract class LocalNewsBaseComponent implements OnDestroy {

  // ==================== ÉTAT COMMUN ====================

  public blobUrl = '';
  public withOtherNews = false;
  public localNews: LocalNewsModel[] = [];
  public currentNewsIndex = 0;
  public currentNews: LocalNewsModel | undefined;
  public futureNews?: LocalNewsModel;
  public videoIsPlaying = false;
  public isTransitionSafetyToSafety = false;
  public count = 0;
  public videoReady = false;
  public startingAnimation = true;

  protected lastTap = 0;
  protected doubleTapDelay = 300;
  protected streamVideoTimeoutId?: number;
  protected videoElement?: HTMLVideoElement;
  protected videoEndedHandler?: () => void;
  protected videoLoadedDataHandler?: () => void;
  protected metadataTimeoutId?: number;
  protected fallbackTimeout?: number;
  protected playbackStartTimeoutId?: number;
  protected destroy$ = new Subject<void>();

  constructor(
    protected sharedService: SharedService,
    protected homeService: HomeService,
    protected logger: ActivityLoggerService,
    protected videoStallHandlerService: VideoStallHandlerService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── À implémenter par chaque composant ──────────────────────
  protected abstract moveToNextNews(): void;

  // ==================== GESTION DES ÉVÉNEMENTS TACTILES ====================

  public onTouchStart(event: TouchEvent): void {
    const currentTime = new Date().getTime();
    const tapLength = currentTime - this.lastTap;
    if (tapLength < this.doubleTapDelay && tapLength > 0) {
      event.preventDefault();
      this.moveToNextNews();
    }
    this.lastTap = currentTime;
  }

  public onTouchEnd(event: TouchEvent): void {
    event.preventDefault();
  }

  // ==================== DURÉE D'AFFICHAGE ====================

  protected getNewsDisplayDuration(): number {
    if (this.currentNews?.tag === 'safety') return 12000;
    if (this.currentNews?.withAnimation) return 16000;
    return 8000;
  }

  public isTheNextNewsSafety(): boolean {
    return this.localNews[(this.count + 1) % this.localNews.length]?.tag === 'safety';
  }

  // ==================== GESTION DES VIDÉOS ====================

  public displayStreamVideo(): void {
    this.clearVideoTimeout();
    this.streamVideoTimeoutId = window.setTimeout(() => {
      this.videoElement = document.getElementById('videoLocal') as HTMLVideoElement;
      if (this.videoElement && !this.videoIsPlaying) {
        this.videoElement.currentTime = 0;
        this.videoElement.onerror = (e) => {
          this.logger.logError('Video load error', e.toString());
          this.moveToNextNews();
        };
        this.fallbackTimeout = window.setTimeout(() => {
          if (!this.videoReady) this.moveToNextNews();
        }, 8000);
        this.setupCanplayListener(this.videoElement);
      }
    }, ANIMATION_VIDEO_DELAY);
  }

  public onVideoMetadataLoaded(): void {
    this.clearMetadataTimeout();
    this.metadataTimeoutId = window.setTimeout(() => {
      this.videoReady = true;
      this.playbackStartTimeoutId = window.setTimeout(() => {
        if (!this.videoIsPlaying) this.moveToNextNews();
      }, 10000);
      if (this.videoElement && this.videoElement.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
        this.playVideoIfReady();
      } else {
        this.setupCanplayListener(this.videoElement ?? null);
      }
    }, 5000);
  }

  // ==================== HELPERS VIDÉO ====================

  protected setupCanplayListener(target: HTMLVideoElement | null): void {
    if (!target) return;
    if (this.videoLoadedDataHandler) {
      target.removeEventListener('canplay', this.videoLoadedDataHandler);
    }
    this.videoLoadedDataHandler = () => this.playVideoIfReady();
    target.addEventListener('canplay', this.videoLoadedDataHandler, { once: true });
  }

  protected playVideoIfReady(): void {
    this.videoElement = document.getElementById('videoLocal') as HTMLVideoElement;
    if (this.videoElement && this.videoReady && !this.videoIsPlaying) {
      this.fallbackTimeout        = this.clearTimer(this.fallbackTimeout);
      this.playbackStartTimeoutId = this.clearTimer(this.playbackStartTimeoutId);
      this.videoEndedHandler = () => this.moveToNextNews();
      this.videoElement.addEventListener('ended', this.videoEndedHandler);
      this.videoIsPlaying = true;
      this.videoStallHandlerService.attachStallHandlers(this.videoElement, () => this.moveToNextNews());
      this.videoElement.muted = true;
      this.videoElement.play().catch(() => this.moveToNextNews());
    }
  }

  // ==================== NETTOYAGE ET TIMEOUTS ====================

  /** clearTimeout(undefined) est un no-op natif — pas besoin de guard if. */
  protected clearTimer(id: number | undefined): undefined {
    window.clearTimeout(id);
    return undefined;
  }

  protected clearVideoTimeout(): void {
    this.streamVideoTimeoutId   = this.clearTimer(this.streamVideoTimeoutId);
    this.fallbackTimeout        = this.clearTimer(this.fallbackTimeout);
    this.playbackStartTimeoutId = this.clearTimer(this.playbackStartTimeoutId);
    this.cleanupVideoListeners();
  }

  protected clearMetadataTimeout(): void {
    this.metadataTimeoutId = this.clearTimer(this.metadataTimeoutId);
  }

  protected cleanupVideoListeners(): void {
    if (this.videoElement) {
      if (this.videoEndedHandler)
        this.videoElement.removeEventListener('ended', this.videoEndedHandler);
      if (this.videoLoadedDataHandler) {
        this.videoElement.removeEventListener('loadeddata', this.videoLoadedDataHandler);
        this.videoElement.removeEventListener('canplay', this.videoLoadedDataHandler);
      }
      this.videoElement.onerror = null;
      this.videoElement = undefined;
      this.videoEndedHandler = undefined;
      this.videoLoadedDataHandler = undefined;
    }
  }
}
